/**
 * 配镜流程「默认勾选」依赖的本地记忆（localStorage），用于跨步骤恢复上次选择。
 * 常规配镜与仅镜框配镜共用 `zl-prevLensProcessData`。
 */

export interface PrevLensSelection {
  prevPrescriptionType: string;
  /** 处方填写方式 PRESCRIPTION_METHODS */
  prevFillPrescriptionMethod?: number | '';
  /**
   * 镜片类型步「类型 + 品牌标题 + 品牌名称 + 颜色」：`encodeURIComponent` 各段后用 `#` 拼接，避免段内含 `#` 误分割
   */
  prevLensType: string;
  /**
   * 折射率步「折射率 + 品牌」：`encodeURIComponent(refractive)#encodeURIComponent(brand)`
   */
  prevLensIndexKey: string;
  prevViewArea: string | null;
  prevCoatingId: string;
}

const STORAGE_KEY = 'zl-prevLensProcessData';

const EMPTY: PrevLensSelection = {
  prevPrescriptionType: '',
  prevFillPrescriptionMethod: '',
  prevLensType: '',
  prevLensIndexKey: '',
  prevViewArea: null,
  prevCoatingId: '',
};

const PREV_FIELD_SEP = '#';

/** 写入 localStorage 前编码镜片类型步选中（与 `getPrevLensTypeSelectionTriple` 对偶） */
export function encodePrevLensTypeSelection(
  lensTypeKey: string,
  brandTitle: string,
  lensColor: string,
  brandName = '',
): string {
  return [lensTypeKey, brandTitle, brandName, lensColor]
    .map((s) => encodeURIComponent(s ?? ''))
    .join(PREV_FIELD_SEP);
}

/** 解码 `prevLensType` 供 `resolveLensTypeColorSelection`；兼容旧版「类型 + 品牌标题 + 颜色」三段值 */
export function getPrevLensTypeSelectionTriple(prev: PrevLensSelection): {
  lensTypeKey: string;
  brandTitle: string;
  brandName: string;
  lensColor: string;
} {
  if (!prev.prevLensType) {
    return { lensTypeKey: '', brandTitle: '', brandName: '', lensColor: '' };
  }
  const parts = prev.prevLensType.split(PREV_FIELD_SEP);
  if (parts.length !== 3 && parts.length !== 4) {
    return { lensTypeKey: '', brandTitle: '', brandName: '', lensColor: '' };
  }
  try {
    const [lensTypeKey, brandTitle, brandName, lensColor] =
      parts.length === 4 ? parts : [parts[0], parts[1], '', parts[2]];

    return {
      lensTypeKey: decodeURIComponent(lensTypeKey),
      brandTitle: decodeURIComponent(brandTitle),
      brandName: decodeURIComponent(brandName),
      lensColor: decodeURIComponent(lensColor),
    };
  } catch {
    return { lensTypeKey: '', brandTitle: '', brandName: '', lensColor: '' };
  }
}

/** 写入 localStorage 前编码折射率步选中（与 `decodePrevLensIndexKey` 对偶） */
export function encodePrevLensIndexKey(refractiveIndex: string, indexBrand: string): string {
  return [refractiveIndex, indexBrand].map((s) => encodeURIComponent(s ?? '')).join(PREV_FIELD_SEP);
}

/** 解码 `prevLensIndexKey`；非法或空则 refractiveIndex、indexBrand 均为 `''` */
export function decodePrevLensIndexKey(prevLensIndexKey: string): {
  refractiveIndex: string;
  indexBrand: string;
} {
  const parts = prevLensIndexKey?.split(PREV_FIELD_SEP) ?? [];
  if (!prevLensIndexKey || parts.length !== 2) {
    return { refractiveIndex: '', indexBrand: '' };
  }
  try {
    return {
      refractiveIndex: decodeURIComponent(parts[0]),
      indexBrand: decodeURIComponent(parts[1]),
    };
  } catch {
    return { refractiveIndex: '', indexBrand: '' };
  }
}

function normalizePrevLensSelection(raw: Partial<PrevLensSelection>): PrevLensSelection {
  return {
    prevPrescriptionType: raw.prevPrescriptionType ?? '',
    prevFillPrescriptionMethod: raw.prevFillPrescriptionMethod ?? '',
    prevLensType: raw.prevLensType ?? '',
    prevLensIndexKey: raw.prevLensIndexKey ?? '',
    prevViewArea: raw.prevViewArea ?? null,
    prevCoatingId: raw.prevCoatingId ?? '',
  };
}

export function readPrevLensSelection(): PrevLensSelection {
  if (typeof window === 'undefined') {
    return { ...EMPTY };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<PrevLensSelection>;
    return normalizePrevLensSelection(parsed);
  } catch {
    return { ...EMPTY };
  }
}

export function mergePrevLensSelection(patch: Partial<PrevLensSelection>): void {
  if (typeof window === 'undefined') return;
  const current = readPrevLensSelection();
  const next = normalizePrevLensSelection({ ...current, ...patch });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

/** 写入上次折射率选中：`refractive_index#index_brand`（跨镜片类型按规格恢复） */
export function mergePrevLensIndexSelection(item: {
  refractive_index?: string;
  index_brand?: string;
}): void {
  mergePrevLensSelection({
    prevLensIndexKey: encodePrevLensIndexKey(item.refractive_index ?? '', item.index_brand ?? ''),
  });
}
